/**
 * 录音文件存储管理工具
 * 使用 IndexedDB 存储录音 Blob，localStorage 存储元信息
 */

const DB_NAME = 'RecordingDB';
const DB_VERSION = 1;
const STORE_NAME = 'failedRecordings';
const LOCAL_STORAGE_KEY = 'failed_recordings_meta';

// 初始化 IndexedDB
function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

/**
 * 保存失败的录音记录
 * @param {Blob} audioBlob - 录音文件 Blob 对象
 * @param {Object} metadata - 元数据
 * @returns {Promise<string>} 返回记录 ID
 */
export async function saveFailedRecording(audioBlob, metadata = {}) {
  try {
    const db = await initDB();
    const id = `recording_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // 保存到 IndexedDB
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const recordData = {
      id,
      audioBlob,
      createdAt: new Date().getTime()
    };

    await new Promise((resolve, reject) => {
      const request = store.add(recordData);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    // 保存元信息到 localStorage
    const metaList = getFailedRecordingsMeta();
    metaList.push({
      id,
      fileName: metadata.fileName || `录音_${Date.now()}.webm`,
      timestamp: Date.now(),
      chatId: metadata.chatId || '',
      medmstrId: metadata.medmstrId || '',
      error: metadata.error || '上传失败',
      name: metadata.name || '',
      description: metadata.description || '',
      category: metadata.category || '',
      tags: metadata.tags || '',
      role: metadata.role || 'doctor'
    });

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(metaList));

    return id;
  } catch (error) {
    console.error('保存失败录音记录出错：', error);
    throw error;
  }
}

/**
 * 获取所有失败录音的元信息
 * @returns {Array} 元信息列表
 */
export function getFailedRecordingsMeta() {
  try {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('读取失败录音元信息出错：', error);
    return [];
  }
}

/**
 * 根据 ID 获取录音文件 Blob
 * @param {string} id - 记录 ID
 * @returns {Promise<Blob|null>}
 */
export async function getRecordingBlob(id) {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.audioBlob : null);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('获取录音文件出错：', error);
    return null;
  }
}

/**
 * 删除失败的录音记录
 * @param {string} id - 记录 ID
 */
export async function deleteFailedRecording(id) {
  try {
    // 从 IndexedDB 删除
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    await new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    // 从 localStorage 删除元信息
    const metaList = getFailedRecordingsMeta();
    const newMetaList = metaList.filter(item => item.id !== id);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newMetaList));

    return true;
  } catch (error) {
    console.error('删除失败录音记录出错：', error);
    throw error;
  }
}

/**
 * 清理过期的录音记录（超过指定时间的记录）
 * @param {number} maxAge - 最大保存时间（毫秒），默认2小时
 */
export async function cleanExpiredRecordings(maxAge = 2 * 60 * 60 * 1000) {
  try {
    const metaList = getFailedRecordingsMeta();
    const now = Date.now();
    const expiredIds = [];

    metaList.forEach(meta => {
      if (now - meta.timestamp > maxAge) {
        expiredIds.push(meta.id);
      }
    });

    // 删除过期记录
    for (const id of expiredIds) {
      await deleteFailedRecording(id);
    }

    return expiredIds.length;
  } catch (error) {
    console.error('清理过期录音记录出错：', error);
    return 0;
  }
}

/**
 * 根据会话ID获取失败的录音记录
 * @param {string} chatId - 会话 ID
 * @returns {Array} 该会话的失败录音列表
 */
export function getFailedRecordingsByChatId(chatId) {
  const metaList = getFailedRecordingsMeta();
  return metaList.filter(meta => meta.chatId === chatId);
}
